import { AssetDropdownInput } from "#src/batteries-included-components/Dropdowns/AssetDropdownInput";
import type { BaseEntityDropdownProps } from "#src/batteries-included-components/Dropdowns/EntityDropdownInput";
import { useCustomAttributeFiltersV2 } from "#src/components/hooks/FilterPanel/useCustomAttributeFilters";
import { useIsFeatureAvailable } from "#src/contexts/AuthenticatedContext.helpers";
import useLocalization from "#src/hooks/useLocalization";
import {
  TextInput,
  type ControlledTextInputProps,
} from "@validereinc/common-components";
import {
  convertMapToOpts,
  Assets,
  type AssetTypeSelectionType,
  FeatureFlags,
} from "@validereinc/domain-controllers";
import { toTitleCaseString } from "@validereinc/utilities";
import React from "react";

const { AssetTypeSelection } = Assets.view;

export const useGetAssetTypeOptions = () => {
  const { localize } = useLocalization();

  const [isFacilitiesAvailable] = useIsFeatureAvailable({
    featureFlagQuery: FeatureFlags.Facilities,
  });
  const [isEquipmentAvailable] = useIsFeatureAvailable({
    featureFlagQuery: FeatureFlags.Equipment,
  });
  const [isDevicesAvailable] = useIsFeatureAvailable({
    featureFlagQuery: FeatureFlags.Devices,
  });
  const [isFlowsAvailable] = useIsFeatureAvailable({
    featureFlagQuery: FeatureFlags.Flows,
  });
  const [isAssetGroupsAvailable] = useIsFeatureAvailable({
    featureFlagQuery: FeatureFlags.Networks,
  });

  const options = convertMapToOpts(AssetTypeSelection, ([_, value]) =>
    toTitleCaseString(localize(value))
  ).filter((option) => {
    switch (option.value) {
      case AssetTypeSelection.FACILITY:
        return isFacilitiesAvailable;
      case AssetTypeSelection.EQUIPMENT:
        return isEquipmentAvailable;
      case AssetTypeSelection.DEVICE:
        return isDevicesAvailable;
      case AssetTypeSelection.FLOW:
        return isFlowsAvailable;
      case AssetTypeSelection.ASSET_GROUP:
        return isAssetGroupsAvailable;
      case AssetTypeSelection.ALL_ASSETS:
        return true;
      default:
        return false;
    }
  });

  return options;
};

export const useAssetDropdownInput = (
  assetType: AssetTypeSelectionType,
  name: string,
  inputProps?: Partial<BaseEntityDropdownProps>
) => {
  const { localize } = useLocalization();
  const commonProps: BaseEntityDropdownProps = {
    name: assetType === AssetTypeSelection.ALL_ASSETS ? "" : name,
    isFluid: true,
    label: localize(assetType),
    placeholder: `Select ${localize(`${assetType}_plural`)}...`,
  };

  if (assetType === AssetTypeSelection.ALL_ASSETS) return <></>;

  return (
    <AssetDropdownInput
      assetType={assetType}
      {...commonProps}
      {...inputProps}
    />
  );
};

export const useAssetSearchInput = (
  assetType: AssetTypeSelectionType,
  name: string,
  inputProps?: Partial<ControlledTextInputProps<any>>
) => {
  const { localize } = useLocalization();
  const commonProps: ControlledTextInputProps<any> = {
    name: assetType === AssetTypeSelection.ALL_ASSETS ? "" : name,
    label: localize(assetType),
    placeholder: "Search Assets...",
    type: "search",
  };

  if (assetType === AssetTypeSelection.ALL_ASSETS) return <></>;

  return (
    <TextInput
      {...commonProps}
      {...(inputProps ?? {})}
    />
  );
};

export const useAssetCustomAttributeFilters = (
  assetType: AssetTypeSelectionType
) => {
  const filters = useCustomAttributeFiltersV2({
    assetType:
      assetType !== AssetTypeSelection.ALL_ASSETS
        ? assetType
        : AssetTypeSelection.FACILITY,
    prefix: `workflow.${assetType}.custom_attributes`,
  });

  if (assetType === AssetTypeSelection.ALL_ASSETS)
    return {
      customAttributes: [],
      customAttributeHeaders: [],
      customAttributeFilters: [],
      isLoading: false,
    };

  return filters;
};
